/**
 * 保存パイプライン (§4.1/§4.2)。deep watch → Firestore 直結の禁止を実装で強制する:
 * 呼び出し側 (firestore-crud) は markDirty しか呼べず、書込は doc 単位の
 * 2秒 debounce (trailing) 後に flushDoc へ委譲される。
 *
 * flush トリガ (§4.2): debounce 満了 / flushAll (保存ボタン・ルート離脱・エクスポート) /
 * installGlobalTriggers (visibilitychange hidden + pagehide)。
 *
 * 内部状態は doc ごとに独立した DocEntry で管理する (Map<docPath, DocEntry>)。
 * doc 単位で in-flight ロック・version・backoff を持つことで、他 doc の成功/失敗が
 * 巻き込まれたり、flush 中の再編集が消えたりしないようにする。全体の state / dirtyCount は
 * 各 doc の状態から都度導出する (recompute)。
 */
import { ref, type Ref } from 'vue';

export type SaveState = 'idle' | 'dirty' | 'saving' | 'offline' | 'error';

export interface SavePipeline {
  markDirty(docPath: string): void;
  flushAll(): Promise<void>;
  state: Ref<SaveState>;
  dirtyCount: Ref<number>;
  installGlobalTriggers(): () => void;
  /** 当該 doc を恒久エラーとして隔離する: 以後 markDirty されても flush しない。 */
  quarantine(docPath: string): void;
  /** 全 doc の保留デバウンス/バックオフタイマーを止めて状態を破棄する。in-flight な flush は止めない。 */
  dispose(): void;
}

const MAX_BACKOFF_MS = 30_000;
const INITIAL_BACKOFF_MS = 1000;

/** doc は idle (完全に同期済み) になった時点で Map から削除される。'error' (quarantine) のみ永続。 */
interface DocEntry {
  version: number;
  inFlight: Promise<void> | null;
  backoffMs: number;
  timer: ReturnType<typeof setTimeout> | null;
  status: 'dirty' | 'saving' | 'offline' | 'error';
}

export function createSavePipeline(opts: {
  flushDoc: (docPath: string) => Promise<void>;
  debounceMs?: number;
}): SavePipeline {
  const debounceMs = opts.debounceMs ?? 2000;
  const docs = new Map<string, DocEntry>();
  const state = ref<SaveState>('idle');
  const dirtyCount = ref(0);

  function getEntry(docPath: string): DocEntry {
    let entry = docs.get(docPath);
    if (!entry) {
      entry = { version: 0, inFlight: null, backoffMs: INITIAL_BACKOFF_MS, timer: null, status: 'dirty' };
      docs.set(docPath, entry);
    }
    return entry;
  }

  function scheduleFlush(docPath: string, entry: DocEntry, delayMs: number): void {
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = setTimeout(() => void flushOne(docPath), delayMs);
  }

  // await をまたぐと quarantine() が entry.status を非同期に書き換えうるが、TS の制御フロー解析は
  // 呼び出し元スコープでの絞り込み ('saving' 等) を await 後も引きずってしまう。別関数越しに読むことで
  // 絞り込みをリセットし、'error' への遷移を正しく検知できるようにする。
  function statusOf(entry: DocEntry): DocEntry['status'] {
    return entry.status;
  }

  function recompute(): void {
    let anyError = false;
    let anyOffline = false;
    let anySaving = false;
    let anyDirty = false;
    for (const entry of docs.values()) {
      if (entry.status === 'error') anyError = true;
      else if (entry.status === 'offline') anyOffline = true;
      else if (entry.status === 'saving') anySaving = true;
      else if (entry.status === 'dirty') anyDirty = true;
    }
    dirtyCount.value = docs.size;
    if (anyError) state.value = 'error';
    else if (anyOffline) state.value = 'offline';
    else if (anySaving) state.value = 'saving';
    else if (anyDirty) state.value = 'dirty';
    else state.value = 'idle';
  }

  async function runFlush(docPath: string, entry: DocEntry): Promise<void> {
    if (entry.timer) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    const v = entry.version;
    entry.status = 'saving';
    recompute();
    try {
      await opts.flushDoc(docPath);
      if (statusOf(entry) === 'error') {
        // flush 中に quarantine された: 結果を破棄し隔離状態を維持する。
        recompute();
        return;
      }
      entry.backoffMs = INITIAL_BACKOFF_MS;
      if (entry.version === v) {
        // flush 開始時から再編集なし: 完全に同期済みなので Map から削除 (idle)。
        docs.delete(docPath);
      } else {
        // flush 中に再度 markDirty された: 再編集を失わないよう debounce で再スケジュール。
        entry.status = 'dirty';
        scheduleFlush(docPath, entry, debounceMs);
      }
      recompute();
    } catch {
      // ネットワーク等の一時失敗: dirty 保持 + doc 単位バックオフで再試行 (§4.4)。
      // 恒久失敗 (permission-denied 等) の隔離は flushDoc 実装側が quarantine() を呼ぶ責務。
      if (statusOf(entry) === 'error') {
        recompute();
        return;
      }
      entry.status = 'offline';
      scheduleFlush(docPath, entry, entry.backoffMs);
      entry.backoffMs = Math.min(entry.backoffMs * 2, MAX_BACKOFF_MS);
      recompute();
    }
  }

  function flushOne(docPath: string): Promise<void> {
    const entry = docs.get(docPath);
    if (!entry || entry.status === 'error') return Promise.resolve();
    if (entry.inFlight) return entry.inFlight; // 多重flush防止: 実行中なら同じ Promise を返す。
    const promise = runFlush(docPath, entry).finally(() => {
      if (entry.inFlight === promise) entry.inFlight = null;
    });
    entry.inFlight = promise;
    return promise;
  }

  function markDirty(docPath: string): void {
    const entry = getEntry(docPath);
    if (entry.status === 'error') return; // quarantine 済み doc は再編集を無視する。
    entry.version += 1;
    entry.status = 'dirty';
    scheduleFlush(docPath, entry, debounceMs);
    recompute();
  }

  async function flushAll(): Promise<void> {
    // flush 中の再編集を取りこぼさないよう、dirty/saving が尽きるまで複数パス回す。
    // 1パスだけ (Promise.all(paths.map(flushOne)) を await して即 return) だと、in-flight な
    // flush を await している間に markDirty されても、再編集は debounce タイマーの再予約で
    // 終わってしまい、flushAll の resolve 時点では未送信のまま残る。flushAll は pagehide 相当の
    // 離脱時セーフティネットなので、resolve した時点でタイマー予約の再編集を残すのは目的に反する
    // (直後にタブが閉じれば消える)。dirty/saving が残っている限りパスを繰り返し、in-flight の
    // 完了後に dirty に戻った分も同じ flushAll 呼び出し内で送信し切ることでこの窓を閉じる。
    //
    // offline (バックオフ待機中) と error (quarantine) は対象外。offline はバックオフ中に
    // 即リトライしても同じ理由 (ネットワーク等) ですぐ失敗するだけでループを無駄に消費するので、
    // 補償は再送キュー (§4.4) の責務とし、ここでは何もしない (無限ループにはならない: 対象から
    // 除外しているため回数の上限は他の dirty/saving の解消だけで決まる)。error は恒久隔離。
    //
    // 上限10パス: flush中も編集し続けるユーザーへの防御。上限に達しても dirty が残っていれば
    // 諦めてそのまま return する (= 既存の debounce タイマーに委ねる。現行仕様と同じ)。
    for (let pass = 0; pass < 10; pass++) {
      const targets = [...docs.entries()].filter(
        ([, entry]) => statusOf(entry) === 'dirty' || statusOf(entry) === 'saving'
      );
      if (targets.length === 0) break;
      await Promise.all(
        targets.map(([docPath, entry]) => {
          if (entry.timer) {
            clearTimeout(entry.timer);
            entry.timer = null;
          }
          return flushOne(docPath);
        })
      );
    }
  }

  function quarantine(docPath: string): void {
    const entry = getEntry(docPath);
    if (entry.timer) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    entry.status = 'error';
    recompute();
  }

  function dispose(): void {
    // 保留中のデバウンス/バックオフタイマーのみ止める。in-flight な flush (await 中) は
    // 呼び出し元の Promise が既に走っているため止められないし、止める必要もない
    // (完走させて構わない — §指示どおり)。
    for (const entry of docs.values()) {
      if (entry.timer) {
        clearTimeout(entry.timer);
        entry.timer = null;
      }
    }
    docs.clear();
    recompute();
  }

  function installGlobalTriggers(): () => void {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') void flushAll();
    };
    const onPageHide = () => void flushAll();
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
    };
  }

  return { markDirty, flushAll, state, dirtyCount, installGlobalTriggers, quarantine, dispose };
}
